import { useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, Check, Star, Trash2, X } from "lucide-react";
import { PRIORITY_LABEL, STATUS_LABEL, TASK_STATUSES, type Priority, type TaskStatus, type TaskWithFlags } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { useTaskActions, type TaskActions } from "@/data/actions";
import { runBulk } from "@/data/bulk";
import { useHierarchy } from "@/data/hooks";
import { DestinationCommand, INBOX, destLabel, normalizeDest, sameDest, type Dest } from "@/features/destination/DestinationPicker";
import { PRIORITY_ORDER } from "@/features/task/TaskMenu";
import { useUi, ui } from "@/features/ui-state";
import { plural } from "@/lib/utils";
import { captureSelection, clearSelection, reconcileSelection, selectedTasks } from "./selection";

const isOpen = (t: TaskWithFlags) => t.status !== "done" && t.status !== "cancelled";

/**
 * Run one per-task operation over the selection. Failed tasks stay selected and are reported; on
 * full success, `keep` decides whether the selection stays (edits) or clears (rows leave the list).
 */
export async function applyBulk(
  tasks: TaskWithFlags[],
  op: (t: TaskWithFlags) => Promise<unknown>,
  opts: { done: (count: string) => string; keep?: boolean; undo?: (t: TaskWithFlags) => Promise<unknown> },
) {
  if (!tasks.length) return;
  const ownsSelection = captureSelection();
  const { ok, failed } = await runBulk(tasks, op as never);
  const action = opts.undo && ok.length ? { label: "Undo", onClick: () => void runBulk(ok, opts.undo as never) } : undefined;
  if (failed.length) {
    const current = ownsSelection();
    if (current) ui.set({ selectedIds: failed.map((t) => t.id), selectedTaskId: failed[0]!.id, selectionAnchor: failed[0]!.id });
    toast.error(opts.done(`${ok.length} of ${plural(tasks.length, "task")}`), {
      description: current
        ? `${plural(failed.length, "task")} failed and ${failed.length === 1 ? "stays" : "stay"} selected.`
        : `${plural(failed.length, "task")} failed.`,
      action,
    });
    return;
  }
  if (ownsSelection()) {
    if (opts.keep) reconcileSelection();
    else clearSelection();
  }
  toast(opts.done(plural(ok.length, "task")), action ? { action } : undefined);
}

/** Bulk actions shared by the bar and keyboard shortcuts. */
export function bulkActions(a: TaskActions) {
  return {
    complete: (tasks: TaskWithFlags[]) =>
      applyBulk(tasks.filter(isOpen), (t) => a.complete(t, { undo: false }), { done: (n) => `Completed ${n}`, undo: (t) => a.reopen(t) }),
    remove: (tasks: TaskWithFlags[]) => applyBulk(tasks, (t) => a.remove(t, { undo: false }), { done: (n) => `Deleted ${n}`, undo: (t) => a.restore(t) }),
    today: (tasks: TaskWithFlags[]) =>
      applyBulk(
        tasks.filter((t) => isOpen(t) && !a.isPlannedToday(t)),
        (t) => a.planAdd([t.id]),
        { done: (n) => `Added ${n} to Today`, keep: true, undo: (t) => a.planRemove(t) },
      ),
  };
}

export function BulkBar() {
  const ids = useUi((s) => s.selectedIds);
  const h = useHierarchy();
  const a = useTaskActions();
  const [moveOpen, setMoveOpen] = useState(false);
  if (ids.length < 2) return null;
  const tasks = () => selectedTasks();
  const bulk = bulkActions(a);
  const setPriority = (p: Priority) =>
    applyBulk(tasks().filter((t) => t.priority !== p), (t) => a.setPriority(t, p), { done: (n) => `Set ${n} to ${PRIORITY_LABEL[p].toLowerCase()} priority`, keep: true });
  const setStatus = (s: TaskStatus) =>
    applyBulk(
      tasks().filter((t) => t.status !== s),
      (t) => (s === "done" ? a.complete(t, { undo: false }) : a.setStatus(t, s)),
      { done: (n) => `Set ${n} to ${STATUS_LABEL[s].toLowerCase()}`, keep: s !== "done" && s !== "cancelled" },
    );
  const move = (d: Dest) => {
    setMoveOpen(false);
    const list = tasks().filter((t) => !sameDest(normalizeDest(t, h), d));
    void applyBulk(list, (t) => a.move(t, d), {
      done: (n) => `Moved ${n} to ${destLabel(d, h)}`,
      undo: (t) => a.move(t, { areaId: t.areaId, spaceId: t.spaceId, projectId: t.projectId }),
    });
  };

  return (
    <>
      <div
        role="toolbar"
        aria-label={`${ids.length} tasks selected`}
        onKeyDown={(e) => {
          // Let the focused button activate natively instead of triggering list shortcuts.
          if (e.key === " " || e.key === "Enter") e.stopPropagation();
        }}
        className="fixed bottom-14 left-1/2 z-40 flex h-11 -translate-x-1/2 items-center gap-0.5 rounded-xl border border-border bg-popover px-1.5 text-[13px] text-popover-foreground shadow-pop"
      >
        <span className="whitespace-nowrap px-2 font-medium tabular">{ids.length} selected</span>
        <span aria-hidden className="mx-1 h-5 w-px bg-border" />
        <Tooltip content="Add to Today" shortcut="T">
          <Button size="sm" variant="quiet" onClick={() => void bulk.today(tasks())}>
            <Star />
            Today
          </Button>
        </Tooltip>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="quiet">
              <span aria-hidden className="contents">
                <PriorityIcon priority="high" />
              </span>
              Priority
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="center">
            {PRIORITY_ORDER.map((p) => (
              <DropdownMenuItem key={p} onSelect={() => void setPriority(p)}>
                <PriorityIcon priority={p} />
                {PRIORITY_LABEL[p]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="quiet">
              <span aria-hidden className="contents">
                <StatusIcon status="in_progress" />
              </span>
              Status
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="center">
            {TASK_STATUSES.map((s) => (
              <DropdownMenuItem key={s} onSelect={() => void setStatus(s)}>
                <StatusIcon status={s} />
                {STATUS_LABEL[s]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button size="sm" variant="quiet" onClick={() => setMoveOpen(true)}>
          <ArrowRightLeft />
          Move…
        </Button>
        <Tooltip content="Complete" shortcut="X">
          <Button size="sm" variant="quiet" onClick={() => void bulk.complete(tasks())}>
            <Check />
            Complete
          </Button>
        </Tooltip>
        <Tooltip content="Delete" shortcut="⌘⌫">
          <Button size="sm" variant="quiet" className="text-destructive [&_svg]:text-destructive" onClick={() => void bulk.remove(tasks())}>
            <Trash2 />
            Delete
          </Button>
        </Tooltip>
        <span aria-hidden className="mx-1 h-5 w-px bg-border" />
        <Tooltip content="Clear selection" shortcut="Esc">
          <Button size="icon-sm" variant="quiet" aria-label="Clear selection" onClick={clearSelection}>
            <X />
          </Button>
        </Tooltip>
      </div>
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent position="top" hideClose className="max-w-[420px] overflow-hidden p-0">
          <DialogTitle className="sr-only">Move {plural(ids.length, "task")}</DialogTitle>
          <DestinationCommand value={INBOX} onSelect={move} />
        </DialogContent>
      </Dialog>
    </>
  );
}
